import React, { useEffect, useState } from 'react';
import { Input, message } from 'antd';
import { CloseCircleOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';

interface EditableStatusInputProps {
    initialValue?: string;
    placeholder?: string;
    size?: 'small' | 'middle' | 'large';
    onSave: (value: string) => Promise<void> | void;
    initialSaved?: boolean;
}

export const EditableStatusInput: React.FC<EditableStatusInputProps> = ({
    initialValue = '',
    placeholder = 'Enter Custom Information',
    size = 'small',
    onSave,
}) => {
    const [value, setValue] = useState(initialValue);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(!!initialValue.trim());
    const [error, setError] = useState<string | null>(initialValue.trim() ? null : 'Custom name is required');

    useEffect(() => {
        setValue(initialValue);
        if (initialValue.trim()) {
            setSaved(true);
            setError(null);
        }
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setValue(next);
        setSaved(false);
        if (error && next.trim()) {
            setError(null);
        }
    };

    const handleBlur = async () => {
        const trimmed = value.trim();
        if (!trimmed) {
            setError('Custom name is required');
            message.error('Custom name is required');
            return;
        }
        setError(null);
        try {
            setSaving(true);
            await Promise.resolve(onSave(trimmed));
            setSaved(true);
        } finally {
            setSaving(false);
        }
    };

    const statusIcon = saving ? (
        <LoadingOutlined />
    ) : saved && !error ? (
        <CheckCircleOutlined style={{ color: 'var(--green, #16A34A)' }} />
    ) : error ? (
        <CloseCircleOutlined style={{ color: 'var(--red, #DC2626)' }} />
    ) : null;

    const stopBubble = (e: React.SyntheticEvent) => {
        e.stopPropagation();
    };

    return (
        <>
            <Input
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                size={size}
                onMouseDown={stopBubble}
                onClick={stopBubble}
                onFocus={stopBubble}
                status={error ? 'error' : undefined}
                onKeyDown={e => {
                    if (e.key === 'Enter') handleBlur();
                }}
                suffix={
                    statusIcon ? (
                        <span
                            onMouseDown={stopBubble}
                            onClick={stopBubble}
                            aria-hidden
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                            {statusIcon}
                        </span>
                    ) : (
                        <span />
                    )
                }
            />
            {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </>
    );
};

export default EditableStatusInput;
